import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ConvertedMaxCLASetting } from '../../types';

import { schema } from '../../utils/schema';
import { convertPayloadForSave } from '../../utils/utils';

import { ADMIN_CLA_SETTINGS } from 'RestAPIs/endpoints';
import { useSaveResource } from 'RestAPIs/useSaveResource';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';

import { ControlledDatePicker, ControlledTextField } from 'components/shared';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import {
  MaxCLACodeGroupSelect,
  MaxCLATypeGroupSelect
} from './components/MaxCLAGroupSelect';

type CreateEditMaxCLAFormProps = {
  handleClose: () => void;
  rowData?: ConvertedMaxCLASetting;
};

const CreateEditMaxCLAForm = ({
  handleClose,
  rowData
}: CreateEditMaxCLAFormProps) => {
  const { t } = useTranslation();
  const { mutateAsync: save, isLoading } = useSaveResource({
    url: ADMIN_CLA_SETTINGS,
    id: rowData?.claDateId,
    invalidateQueryKey: ['maxCLASettings', 'list']
  });

  const methods = useForm<ConvertedMaxCLASetting>({
    defaultValues: schema.cast(rowData),
    resolver: yupResolver(schema)
  });
  const { control, handleSubmit } = methods;

  const onSubmit = (data) => {
    const payload = convertPayloadForSave(data);
    save(payload).then(handleClose);
  };

  return (
    <>
      <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
        <MultiStepForm.Section
          title={t('max_buh.formSectionLabels.basis')}
          validateFields={['claId', 'type', 'value']}
        >
          <Grid item xs={8}>
            <MaxCLACodeGroupSelect control={control} />
          </Grid>
          <Grid item xs={8}>
            <MaxCLATypeGroupSelect control={control} />
          </Grid>
          <Box display="flex" gap={2.5}>
            <ControlledTextField
              name="value"
              type="number"
              control={control}
              variant="outlined"
              label={t('max_buh.grid.buhSalaryMax')}
              inputProps={{ min: 0, max: 9999.99 }}
            />
          </Box>
        </MultiStepForm.Section>

        <MultiStepForm.Section
          title={t('max_buh.formSectionLabels.validity')}
          validateFields={['valid.from', 'valid.to']}
        >
          <Box display="flex" gap={2.5}>
            <ControlledDatePicker
              control={control}
              name="valid.from"
              variant="outlined"
              label={t('validFrom')}
            />
            <ControlledDatePicker
              control={control}
              name="valid.to"
              variant="outlined"
              label={t('validTo')}
            />
          </Box>
        </MultiStepForm.Section>

        <MultiStepForm.Actions>
          <Box display="flex" ml="auto">
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              autoFocus
              color="dark"
            >
              {t('save')}
            </LoadingButton>
          </Box>
        </MultiStepForm.Actions>
      </MultiStepForm>
    </>
  );
};

export default CreateEditMaxCLAForm;
